import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ToastyService } from 'ng2-toasty';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import { ContextoDeProjetoService } from '../../core/services/contexto-de-projeto.service';
import { ErrorHandlerService } from '../../core/error-handler.service';
import {
  AnalisePrestacaoContasService, ContadoresAnalise, EtapaAnalise, ItemAnalise
} from '../../core/services/prestacaoContas/analisePrestacaoContas.service';
import {
  ChecklistDocumentosService, Fase, ItemChecklist, ResumoChecklist
} from '../../core/services/prestacaoContas/checklistDocumentos.service';

/**
 * Painel da análise da prestação de contas (ADR-0010).
 *
 * Três coisas que o edital pede e que já estavam no banco sem ninguém ver: o contador de
 * itens, o histórico do fluxo de aprovação com o decurso de cada etapa e o checklist de
 * documentos da fase.
 *
 * **Nenhum número é calculado aqui.** Os contadores vêm contados, as durações vêm formatadas
 * com a sua precisão junto e os dias até o vencimento vêm do relógio do servidor — a tela não
 * sabe nem que dia é hoje.
 */
@Component({
  standalone: false,
  selector: 'app-painel-analise-pc',
  templateUrl: './painel-analise.component.html',
})
export class PainelAnalisePcComponent implements OnInit, OnDestroy {

  /** 'itens' | 'historico' | 'checklist' */
  aba = 'itens';

  celebracaoId: string;

  contadores: ContadoresAnalise;
  /** `null` enquanto o servidor não respondeu — a tela não afirma o que não perguntou (ADR-0074 §3.1). */
  itens: ItemAnalise[] = null;

  /** O item cujo fluxo de aprovação está aberto. */
  itemDoHistorico: ItemAnalise;
  etapas: EtapaAnalise[] = [];

  resumoChecklist: ResumoChecklist;
  checklist: ItemChecklist[] = null;
  fases: Fase[] = [];
  faseEscolhida = 'Prestacao_contas';

  carregando = false;

  private destruido = false;

  constructor(
    private analiseService: AnalisePrestacaoContasService,
    private checklistService: ChecklistDocumentosService,
    private contexto: ContextoDeProjetoService,
    private toasty: ToastyService,
    private errorHandler: ErrorHandlerService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    // O projeto aberto vem do serviço, nunca de sessionStorage direto (ADR-0072 §3.2).
    this.celebracaoId = this.contexto.celebracaoId();
    this.carregar();
  }

  ngOnDestroy(): void {
    this.destruido = true;
  }

  private atualizar(acao: () => void): void {
    if (this.destruido) {
      return;
    }
    acao();
    this.changeDetector.detectChanges();
  }

  private falhou(erro: any): void {
    this.atualizar(() => this.carregando = false);
    this.errorHandler.handle(erro);
  }

  trocarAba(aba: string): void {
    this.aba = aba;
    this.carregar();
  }

  carregar(): void {
    if (!this.celebracaoId) {
      // Avisa uma vez só, mesmo com ngOnInit e troca de aba chegando juntos (ADR-0072 §3.4).
      this.contexto.avisarProjetoAusente(
        'Abra uma celebração para ver a análise da prestação de contas.');
      return;
    }
    this.carregando = true;

    const pronto = (acao: () => void) => this.atualizar(() => { acao(); this.carregando = false; });

    if (this.aba === 'itens') {
      this.analiseService.resumo(this.celebracaoId)
        .then(r => pronto(() => {
          this.contadores = r.contadores;
          this.itens = r.itens || [];
        }))
        // Depois de uma falha a tela volta a "não consultei" (ADR-0074 §3.3).
        .catch(e => { this.itens = null; this.falhou(e); });

    } else if (this.aba === 'checklist') {
      Promise.all([
        this.checklistService.porFase(this.celebracaoId, this.faseEscolhida),
        this.fases.length ? Promise.resolve(this.fases) : this.checklistService.fases(),
      ]).then(([checklist, fases]) => pronto(() => {
        this.resumoChecklist = checklist.resumo;
        this.checklist = checklist.itens || [];
        this.fases = fases;
      })).catch(e => { this.checklist = null; this.falhou(e); });

    } else {
      this.atualizar(() => this.carregando = false);
    }
  }

  // =================================================================== histórico

  abrirHistorico(item: ItemAnalise): void {
    this.itemDoHistorico = item;
    this.aba = 'historico';
    this.carregando = true;

    this.analiseService.historico(item.id)
      .then(etapas => this.atualizar(() => {
        this.etapas = etapas || [];
        this.carregando = false;
      }))
      .catch(e => this.falhou(e));
  }

  /** Os tipos de análise presentes no histórico, na ordem em que aparecem. */
  get tiposDoHistorico(): string[] {
    const tipos: string[] = [];
    this.etapas.forEach(e => {
      if (tipos.indexOf(e.tipoAnaliseDescricao) < 0) {
        tipos.push(e.tipoAnaliseDescricao);
      }
    });
    return tipos;
  }

  etapasDoTipo(tipo: string): EtapaAnalise[] {
    return this.etapas.filter(e => e.tipoAnaliseDescricao === tipo);
  }

  /** Alguma etapa exibida tem precisão de dia: a tela precisa explicar por quê. */
  get temEtapaSemHora(): boolean {
    return this.etapas.some(e => e.duracao && e.duracao.precisao === 'SOMENTE_DIA');
  }

  // =================================================================== checklist

  trocarFase(): void {
    this.aba = 'checklist';
    this.carregar();
  }

  classeDaValidade(item: ItemChecklist): string {
    switch (item.situacaoValidade) {
      case 'VENCIDO': return 'label label-danger';
      case 'VENCE_EM_BREVE': return 'label label-warning';
      case 'NAO_INFORMADA': return 'label label-warning';
      case 'NAO_ENTREGUE': return 'label label-default';
      case 'VIGENTE': return 'label label-success';
      default: return 'label label-default';
    }
  }

  classeDaSituacao(item: ItemAnalise): string {
    switch (item.situacao) {
      case 'COM_PENDENCIA': return 'label label-danger';
      case 'SEM_PENDENCIA': return 'label label-success';
      case 'ATUALIZADO_APOS_CORRECAO': return 'label label-info';
      default: return 'label label-default';
    }
  }

  // =================================================================== PDF

  /**
   * O extrato da análise, com os contadores no cabeçalho (POC 59).
   *
   * Os cinco números impressos são os mesmos objetos que a tela mostra — não há uma segunda
   * contagem para o PDF, que é como as quinze cópias do relatório de receita e despesa
   * divergiram entre si (ADR-0009).
   */
  exportarPdf(): void {
    if (!this.contadores) {
      this.toasty.warning('Abra a aba de itens antes de gerar o PDF.');
      return;
    }
    (pdfMake as any).vfs = (pdfFonts as any).pdfMake
      ? (pdfFonts as any).pdfMake.vfs
      : ((pdfFonts as any).vfs || pdfFonts);

    const c = this.contadores;

    (pdfMake as any).createPdf({
      content: [
        { text: 'Extrato da análise da prestação de contas', style: 'titulo' },
        {
          table: {
            widths: ['*', 60],
            body: [
              ['Total de itens', { text: String(c.total), alignment: 'right' }],
              ['Não verificados', { text: String(c.naoVerificados), alignment: 'right' }],
              ['Com pendência', { text: String(c.comPendencia), alignment: 'right' }],
              ['Sem pendência', { text: String(c.semPendencia), alignment: 'right' }],
              ['Atualizados após correção',
                { text: String(c.atualizadosAposCorrecao), alignment: 'right' }],
              [{ text: 'Soma das situações', bold: true },
               { text: String(c.somaDasSituacoes), alignment: 'right', bold: true }],
            ],
          },
          fontSize: 10, margin: [0, 0, 0, 14],
        },
        { text: 'Itens', style: 'secao' },
        {
          table: {
            headerRows: 1, widths: [50, '*', 90, 110, 45],
            body: [
              [{ text: 'Nº OB', bold: true }, { text: 'Tipo', bold: true },
               { text: 'Valor', bold: true, alignment: 'right' },
               { text: 'Situação', bold: true }, { text: 'Etapas', bold: true }],
              ...(this.itens || []).map(i => [
                i.numeroOb || '—',
                i.tipo,
                { text: this.moeda(i.valor), alignment: 'right' },
                i.situacaoDescricao,
                String(i.analisesRegistradas),
              ]),
            ],
          },
          fontSize: 8,
        },
      ],
      styles: {
        titulo: { fontSize: 14, bold: true, margin: [0, 0, 0, 10] },
        secao: { fontSize: 11, bold: true, margin: [0, 8, 0, 4] },
      },
    }).download(`analise-prestacao-contas-${new Date().toISOString().substring(0, 10)}.pdf`);
  }

  // =================================================================== apoio

  moeda(valor: number): string {
    if (valor === null || valor === undefined) {
      return 'R$ 0,00';
    }
    return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}
